// ./src/components/features/Notes/NoteEditDrawer/NoteEditDrawer.tsx
//
// Create/edit drawer for an event Note. Self-loading in edit mode via recordId;
// context-or-props resolution; no success toast (the page highlight is the
// confirmation). Fields: title, message-type (rich text / markdown), message
// (the matching editor), tags, private flag, status. `eventId` is set on CREATE
// only. At least one of title / message must be filled (server-enforced).
// Mirrors PoiEditDrawer.

import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { Stack, TextInput, Input, SegmentedControl, Switch, Skeleton, Text, Group, Box, ActionIcon, Tooltip, Button } from "@mantine/core";
import { StackedModal } from "@em/ui";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";

import { Note, NoteInput, NOTE_MESSAGE_TYPE } from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalNotesContext } from "@/contexts/NotesContext";
import { AppDrawer, TagsSelectorInput, AppIcon } from "@/components/generic";

// The two body editors are heavy (Tiptap / CodeMirror). Lazy-load both by path so
// they don't leak into the shared bundle — only the chosen one loads.
const RichTextField = React.lazy(() =>
  import("@/components/generic/RichTextField/RichTextField").then((m) => ({ default: m.RichTextField }))
);
const MarkdownField = React.lazy(() =>
  import("@/components/generic/MarkdownField/MarkdownField").then((m) => ({ default: m.MarkdownField }))
);

const MAX_NOTE_TAGS = 10;

export interface NoteEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** The event this note belongs to — set on the input in CREATE mode only. */
  eventId?: string | null;
  onCreated?: (note: Note) => void;
  onUpdated?: (note: Note) => void;
}

interface FormData {
  title: string;
  message: string;
  messageType: string;
  tagIds: string[];
  isPrivate: boolean;
}

const KNOWN_FORM_FIELDS = ["title", "message", "messageType", "tagIds", "isPrivate"];

const getInitialFormData = (): FormData => ({
  title: "",
  message: "",
  messageType: NOTE_MESSAGE_TYPE.RICH_TEXT,
  tagIds: [],
  isPrivate: false,
});

const fromRecord = (record: Note): FormData => ({
  title: record.title || "",
  message: record.message || "",
  messageType: record.messageType || NOTE_MESSAGE_TYPE.RICH_TEXT,
  tagIds: (record.tags || []).map((tag) => tag.id).filter((id): id is string => !!id),
  isPrivate: !!record.isPrivate,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const NoteEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={36} radius="sm" />
    <Skeleton height={200} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const NoteEditDrawer: React.FC<NoteEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  eventId,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const ctx = useOptionalNotesContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<Note | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Note);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record) : getInitialFormData();
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const isMarkdown = formData.messageType === NOTE_MESSAGE_TYPE.MARKDOWN;

  // Full-screen message editing — a larger, distraction-free surface. Only one
  // editor instance is mounted at a time (inline OR modal), so the two never
  // fight over focus/content.
  const [messageFullscreen, setMessageFullscreen] = useState(false);

  useEffect(() => {
    if (!opened) {
      setMessageFullscreen(false);
    }
  }, [opened]);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    // Always send title + message so the server's "title or message" rule runs.
    const submitData: NoteInput = {
      title: formData.title.trim() || null,
      message: formData.message.trim() || null,
      messageType: formData.messageType,
      tagIds: formData.tagIds,
      isPrivate: formData.isPrivate,
    };

    if (!isEditMode) {
      submitData.eventId = eventId ?? null;
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as Note[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, eventId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    // While the full-screen editor is open it owns Esc / ⌘S — don't let the
    // drawer's shortcuts fire underneath it.
    enabled: opened && !messageFullscreen,
  });

  const messagePlaceholder = t("events:notes.form.fields.message.placeholder", {
    defaultValue: "Write your note…",
  });

  const messageTypeToggle = (
    <SegmentedControl
      value={formData.messageType}
      onChange={(value) => setField("messageType", value)}
      size="md"
      data={[
        {
          value: NOTE_MESSAGE_TYPE.RICH_TEXT,
          label: t("events:notes.form.messageType.richText", { defaultValue: "Rich text" }),
        },
        {
          value: NOTE_MESSAGE_TYPE.MARKDOWN,
          label: t("events:notes.form.messageType.markdown", { defaultValue: "Markdown" }),
        },
      ]}
    />
  );

  const renderMessageEditor = (minHeight: number) => (
    <Suspense fallback={<Skeleton height={minHeight} radius="sm" />}>
      {isMarkdown ? (
        <MarkdownField
          value={formData.message}
          onChange={(md) => setField("message", md)}
          placeholder={messagePlaceholder}
          minHeight={minHeight}
        />
      ) : (
        <RichTextField
          value={formData.message}
          onChange={(html) => setField("message", html)}
          placeholder={messagePlaceholder}
          minHeight={minHeight}
        />
      )}
    </Suspense>
  );

  const drawerTitle = isEditMode
    ? t("events:notes.form.editTitle", { defaultValue: "Edit note" })
    : t("events:notes.form.addTitle", { defaultValue: "Add note" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <NoteEditDrawerSkeleton />
      ) : (
        <>
        <Stack gap="md">
          <TextInput
            label={t("events:notes.form.fields.title.label", { defaultValue: "Title" })}
            placeholder={t("events:notes.form.fields.title.placeholder", {
              defaultValue: "A short title (optional)",
            })}
            value={formData.title}
            onChange={(e) => setField("title", e.currentTarget.value)}
            error={fieldErrors.title}
            data-autofocus
            size="md"
          />

          <Input.Wrapper
            label={t("events:notes.form.fields.message.label", { defaultValue: "Message" })}
            description={t("events:notes.form.fields.message.description", {
              defaultValue: "Add a title or a message (at least one is required).",
            })}
            // ⚠ The rich-text/markdown toggle is a bare SegmentedControl inside this wrapper and takes no `error`
            // of its own. `getFormErrors` puts a KNOWN name in `fields` and NEVER in `genericErrors`, so a
            // refusal about `messageType` would vanish entirely — it folds onto the block it belongs to.
            error={fieldErrors.message || fieldErrors.messageType}
          >
            <Stack gap="xs" mt={4}>
              <Group justify="space-between" align="center" wrap="nowrap">
                {messageTypeToggle}
                <Tooltip
                  label={t("events:notes.form.message.fullscreen", { defaultValue: "Edit full screen" })}
                  withArrow
                >
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => setMessageFullscreen(true)}
                    aria-label={t("events:notes.form.message.fullscreen", { defaultValue: "Edit full screen" })}
                  >
                    <AppIcon iconName="Maximize" size={18} />
                  </ActionIcon>
                </Tooltip>
              </Group>

              {messageFullscreen ? (
                <Text size="sm" c="dimmed" py="md">
                  {t("events:notes.form.message.editingFullscreen", { defaultValue: "Editing in full screen…" })}
                </Text>
              ) : (
                renderMessageEditor(220)
              )}
            </Stack>
          </Input.Wrapper>

          <TagsSelectorInput
            label={t("events:notes.form.fields.tags.label", { defaultValue: "Tags" })}
            value={formData.tagIds}
            onChange={(_name, tagIds) => setField("tagIds", tagIds)}
            maxTags={MAX_NOTE_TAGS}
            error={fieldErrors.tagIds}
            size="md"
          />

          <Switch
            label={t("events:notes.form.fields.isPrivate.label", { defaultValue: "Private note" })}
            description={
              <Text component="span" size="xs" c="dimmed">
                {t("events:notes.form.fields.isPrivate.description", {
                  defaultValue: "Only you can see and edit a private note. Public notes are shared with your team.",
                })}
              </Text>
            }
            checked={formData.isPrivate}
            onChange={(e) => setField("isPrivate", e.currentTarget.checked)}
            error={fieldErrors.isPrivate}
            thumbIcon={
              formData.isPrivate ? (
                <AppIcon iconName="Lock" size={12} color="var(--mantine-color-brand-6)" />
              ) : undefined
            }
            size="md"
          />
        </Stack>

        <StackedModal
          opened={messageFullscreen}
          onClose={() => setMessageFullscreen(false)}
          fullScreen
          radius={0}
          transitionProps={{ transition: "fade", duration: 150 }}
          title={t("events:notes.form.message.label", { defaultValue: "Message" })}
        >
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              {messageTypeToggle}
              <Button
                variant="light"
                size="md"
                leftSection={<AppIcon iconName="Minimize" size={16} />}
                onClick={() => setMessageFullscreen(false)}
              >
                {t("events:notes.form.message.done", { defaultValue: "Done" })}
              </Button>
            </Group>

            <Box>{messageFullscreen ? renderMessageEditor(560) : null}</Box>
          </Stack>
        </StackedModal>
        </>
      )}
    </AppDrawer>
  );
};
